import React, { useState, useMemo, useEffect } from "react";
import { FlowView } from "./FlowView";
import { Cursor } from "./Cursor";
import { FilterSidebar } from "../ui/sidebar/FilterSidebar";
import ImagesView from "../search/views/ImagesView";
import { useLazyImages } from "../../hooks/gufram/useLazyImages";
import { useFlowView } from "../../hooks/gufram/useFlowView";
import { useViewSwitch } from "../../hooks/gufram/useViewSwitch";
import { useCursor } from "../../hooks/gufram/useCursor";

// Position classes for FlowView - 12 positions in a 3x4 grid
const POSITION_CLASSES = [
  "position-a1",
  "position-b3",
  "position-c2",
  "position-a4",
  "position-b1",
  "position-c4",
  "position-a2",
  "position-b4",
  "position-c1",
  "position-a3",
  "position-b2",
  "position-c3",
];

// Base URL for hosted Gufram images
const IMAGE_BASE_URL = 'https://propshare.josh-w-martin.com';

// Transform relative image paths to absolute URLs
const resolveImageUrl = (src) => {
  if (!src) return src;
  if (src.startsWith('http') || src.startsWith('data:')) return src;
  // Convert ../wp-content/... to absolute URL
  if (src.startsWith('../')) {
    return `${IMAGE_BASE_URL}/${src.replace('../', '')}`;
  }
  if (src.startsWith('/')) {
    return `${IMAGE_BASE_URL}${src}`;
  }
  return `${IMAGE_BASE_URL}/${src}`;
};

// Filter helper functions
const createFilterDefaults = () => ({
  search: "",
  name: "",
  warehouse: [],
  dominantColor: [],
  usage: [],
  usageTerms: [],
  usageCategory: [],
  size: [],
  material: [],
  condition: [],
  consumable: [],
  historicalPeriod: [],
  historicalRegion: []
});

const normalizeText = (value) =>
  value === null || value === undefined
    ? ""
    : String(value).toLowerCase().trim();

const mulberry32 = (seed) => {
  let state = seed;
  return () => {
    state += 0x6d2b79f5;
    let t = state;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
};

const shuffleWithSeed = (list, seed) => {
  const next = [...list];
  const rng = mulberry32(seed || 1);
  for (let i = next.length - 1; i > 0; i -= 1) {
    const j = Math.floor(rng() * (i + 1));
    [next[i], next[j]] = [next[j], next[i]];
  }
  return next;
};

// Gufram filter field normalization helpers
const deriveIndex = (value, length) => {
  if (!value || length === 0) return 0;
  let total = 0;
  for (let i = 0; i < value.length; i += 1) {
    total += value.charCodeAt(i);
  }
  return total % length;
};

const getPlaceholderField = (itemId, options) => {
  if (!options.length) return "";
  return options[deriveIndex(itemId, options.length)];
};

const detectColor = (item) => {
  const haystack = [
    item.dominantColor,
    item.dominant_color,
    Array.isArray(item.colors) ? item.colors.join(" ") : "",
    item.title,
    item.productName,
    item.product_name,
    item.images && item.images[0] ? item.images[0].src : "",
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();

  const colors = [
    "black",
    "white",
    "gray",
    "grey",
    "red",
    "green",
    "blue",
    "yellow",
    "orange",
    "pink",
    "brown",
    "cream",
  ];
  const match = colors.find((color) => haystack.includes(color));
  if (!match) return "";
  return match === "grey" ? "gray" : match;
};

/**
 * GuframPropBrowser - Wrapper component that integrates Gufram's GridView and FlowView
 * with PropShare's cart system.
 * 
 * Props:
 * - items: Array of prop items (will be transformed to Gufram's expected shape)
 * - onAddToBasket: Callback when user clicks "Add to basket"
 * - onRemoveFromBasket: Callback when user clicks "Remove from basket"
 * - isItemInBasket: Function to check if item is already in basket
 * - onItemSelect: Optional callback when item is selected/opened
 * - defaultView: 'flow' or 'grid' (default: 'flow')
 */
export function GuframPropBrowser({ 
  items = [], 
  onAddToBasket,
  onRemoveFromBasket,
  isItemInBasket,
  onItemSelect,
  onPropDetailsRequest,
  defaultView = 'flow',
}) {
  const [activeItemId, setActiveItemId] = useState(null);
  
  // Filter state
  const [flowOrder, setFlowOrder] = useState("default");
  const [shuffleSeed, setShuffleSeed] = useState(1);
  const [filters, setFilters] = useState(createFilterDefaults);
  
  // Transform items to Gufram's expected shape
  // Original Gufram items already have the right format, PropShare items need transformation
  const guframItems = useMemo(() => {
    return items.map((item, index) => {
      const source = item._original || item;
      const normalizeList = (value) => {
        if (!value) return [];
        if (Array.isArray(value)) return value.map(v => String(v).trim()).filter(Boolean);
        return String(value)
          .split(',')
          .map(v => v.trim())
          .filter(Boolean);
      };
      const consumableLabel = (value) => {
        if (value === true || value === 'true' || value === 1) return 'Yes';
        if (value === false || value === 'false' || value === 0) return 'No';
        return '';
      };
      const colorsList = normalizeList(source.colors || source.dominant_color || source.dominantColor);
      const materialsList = normalizeList(source.material);
      const usageCategoryLabel = source.usage_category_name || source.usage_category || source.category;
      const conditionValue = source.condition;
      const historicalPeriodValue = source.historical_period;
      const historicalRegionValue = source.historical_region;
      const consumableValue = consumableLabel(source.is_consumable);
      // Check if this is already a Gufram-formatted item (has maskSvg, images with src/shadowSrc)
      const isGuframFormat = item.maskSvg || (item.images?.[0]?.src !== undefined);
      
      // Assign position class if not present (cycle through 12 positions)
      const positionClass = item.positionClass || POSITION_CLASSES[index % POSITION_CLASSES.length];
      
      if (isGuframFormat) {
        // Already in Gufram format, resolve image URLs and normalize filter fields
        const normalized = {
          ...item,
          positionClass,
          images: (item.images || []).map(img => ({
            ...img,
            src: resolveImageUrl(img.src),
            shadowSrc: resolveImageUrl(img.shadowSrc),
          })),
          placeholder: resolveImageUrl(item.placeholder),
          _original: item._original || item,
        };
        
        // Normalize filter fields for Gufram data
        return {
          ...normalized,
          productName: normalized.productName || normalized.product_name || normalized.title || '',
          warehouse: normalized.warehouse || normalized.author || '',
          dominantColor: normalized.dominantColor || normalized.dominant_color || detectColor(normalized),
          colors: colorsList,
          material: materialsList,
          usage: normalized.usage || getPlaceholderField(normalized.id, ['living', 'gallery', 'office', 'outdoor']),
          usageCategory: usageCategoryLabel,
          size: normalized.size || getPlaceholderField(normalized.id, ['small', 'medium', 'large']),
          condition: conditionValue || normalized.condition,
          consumable: consumableValue || (normalized.is_consumable ? 'Yes' : 'No'),
          historicalPeriod: historicalPeriodValue || normalized.historical_period,
          historicalRegion: historicalRegionValue || normalized.historical_region,
        };
      }
      
      // PropShare format - transform it
      const images = (item.images || []).map(img => ({
        src: typeof img === 'string' ? img : img.src || img.url,
        shadowSrc: null,
      }));
      
      const placeholder = images[0]?.src || '/images/placeholder.png';
      
      return {
        id: item.id,
        slug: item.slug || item.id,
        title: item.name || item.title || 'Untitled',
        productName: item.name || item.product_name || '',
        author: item.organization?.name || item.owner || item.author || '',
        warehouse: item.organization?.name || item.owner || item.warehouse || '',
        dominantColor: colorsList[0] || item.dominant_color || item.dominantColor || '',
        colors: colorsList,
        material: materialsList,
        usage: item.usage || item.category || '',
        usageCategory: usageCategoryLabel,
        size: item.size || '',
        placeholder: placeholder,
        maskSvg: item.mask_svg || item.maskSvg || null,
        activeImageIndex: 0,
        images: images,
        positionClass,
        condition: conditionValue,
        consumable: consumableValue,
        historicalPeriod: historicalPeriodValue,
        historicalRegion: historicalRegionValue,
        _original: item,
      };
    });
  }, [items]);

  // Standard filtering logic
  const standardFilteredItems = useMemo(() => {
    const searchTerm = normalizeText(filters.search);
    const nameTerm = normalizeText(filters.name);
    return guframItems.filter((item) => {
      const matchesSearch =
        !searchTerm ||
        normalizeText(
          [
            item.title,
            item.productName,
            item.author,
            item.warehouse,
            item.dominantColor,
            item.usage,
            item.size,
          ]
            .filter(Boolean)
            .join(" "),
        ).includes(searchTerm);

      const matchesName =
        !nameTerm ||
        normalizeText([item.productName, item.title].filter(Boolean).join(" "))
          .includes(nameTerm);

      const matchesSelection = (value, selected) => {
        if (!selected || !selected.length) return true;
        const values = Array.isArray(value)
          ? value
          : value !== null && value !== undefined && value !== ''
            ? [value]
            : [];
        const normalizedValues = values
          .map(v => normalizeText(v))
          .filter(Boolean);
        if (!normalizedValues.length) return false;
        return selected.some((entry) =>
          normalizedValues.includes(normalizeText(entry))
        );
      };

      return (
        matchesSearch &&
        matchesName &&
        matchesSelection(item.warehouse, filters.warehouse) &&
        matchesSelection(item.colors, filters.dominantColor) &&
        matchesSelection(item.usage, filters.usage) &&
        matchesSelection(item.usageCategory, filters.usageCategory) &&
        matchesSelection(item.size, filters.size)
        && matchesSelection(item.material, filters.material)
        && matchesSelection(item.condition, filters.condition)
        && matchesSelection(item.consumable, filters.consumable)
        && matchesSelection(item.historicalPeriod, filters.historicalPeriod)
        && matchesSelection(item.historicalRegion, filters.historicalRegion)
      );
    });
  }, [guframItems, filters]);

  // Select filtered items
  const filteredItems = standardFilteredItems;

  // Apply random shuffle if needed
  const orderedItems = useMemo(() => {
    if (flowOrder !== "random") return filteredItems;
    return shuffleWithSeed(filteredItems, shuffleSeed);
  }, [filteredItems, flowOrder, shuffleSeed]);

  // Build flow items with position classes
  const flowItems = useMemo(() => {
    return orderedItems.map((item, index) => ({
      ...item,
      positionClass: POSITION_CLASSES[index % POSITION_CLASSES.length],
    }));
  }, [orderedItems]);

  // Generate a key for lazy image dependency
  const filteredKey = useMemo(
    () => filteredItems.map((item) => item.id || item.slug).join("|"),
    [filteredItems],
  );
  const flowKey = useMemo(
    () => flowItems.map((item) => item.id || item.slug).join("|"),
    [flowItems],
  );

  // Initialize default view
  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.hpview = defaultView;
    }
  }, [defaultView]);

  // Initialize hooks
  useLazyImages(filteredKey);
  useFlowView(flowKey);
  useViewSwitch();
  useCursor();

  // Filter handlers
  const handleFlowOrderChange = (next) => {
    setFlowOrder(next);
    if (next === "random") {
      setShuffleSeed(Math.floor(Math.random() * 1_000_000_000));
    }
  };

  const handleFiltersChange = (newFilters) => {
    setFilters(newFilters);
  };

  // Shuffle handler from global event
  useEffect(() => {
    const handleShuffle = () => {
      handleFlowOrderChange("random");
    };
    window.addEventListener("hpshuffle", handleShuffle);
    return () => {
      window.removeEventListener("hpshuffle", handleShuffle);
    };
  }, []);

  // Handlers
  const handleItemOpen = (item) => {
    const id = item.id || item.slug;
    setActiveItemId(id);
    onItemSelect?.(item._original || item);
    // Add classes to body for cursor hiding and flow pause
    document.body.classList.add('-peek-open', '-flow-paused');
  };

  const handleItemClose = () => {
    setActiveItemId(null);
    document.body.classList.remove('-peek-open', '-flow-paused');
  };

  const handleItemMore = (item) => {
    onPropDetailsRequest?.(item || item._original);
  };

  const handleAddToBasket = (item) => {
    // Pass the original PropShare item back to the cart system
    const originalItem = item._original || item;
    
    // Check if item is already in basket
    const itemId = originalItem.id || item.id;
    const inBasket = isItemInBasket?.(itemId);
    
    if (inBasket) {
      // Remove from basket
      onRemoveFromBasket?.(originalItem);
    } else {
      // Add to basket
      onAddToBasket?.(originalItem);
    }
  };

  // Close peek on outside click
  useEffect(() => {
    if (!activeItemId) return;

    const handlePointerDown = (event) => {
      if (!(event.target instanceof Element)) return;
      if (event.target.closest(".g-item-peek")) return;
      if (event.target.closest(".g-grid__card")) return;
      if (event.target.closest(".g-flow__card")) return;
      setActiveItemId(null);
      document.body.classList.remove('-peek-open', '-flow-paused');
    };

    document.addEventListener("pointerdown", handlePointerDown, true);
    return () => {
      document.removeEventListener("pointerdown", handlePointerDown, true);
    };
  }, [activeItemId]);

  if (!guframItems.length) {
    return (
      <div className="gufram-browser gufram-browser--empty">
        <p>No props to display</p>
      </div>
    );
  }

  return (
    <div className="gufram-browser">
      <Cursor />
      <FilterSidebar
        items={guframItems}
        filters={filters}
        onFiltersChange={handleFiltersChange}
        resultsCount={filteredItems.length}
      />
      <FlowView
        items={flowItems}
        activeItemId={activeItemId}
        onItemOpen={handleItemOpen}
        onItemClose={handleItemClose}
        onItemMore={handleItemMore}
        onAddToBasket={handleAddToBasket}
        isItemInBasket={(itemId) => isItemInBasket?.(itemId)}
      />
      <div className="gufram-grid-wrapper" js-grid="true">
        <ImagesView
          paginatedProps={filteredItems.map(item => {
            const original = item._original || item;
            const resolvedOwner = original.owner || original.organization?.name || original.warehouse || 'Unknown Warehouse';
            const resolvedLocation = original.location || original.organization?.location || '';
            // Ensure images is an array of strings for ImagesView
            return {
              ...original,
              owner: resolvedOwner,
              location: resolvedLocation,
              images: item.images?.map(img => typeof img === 'string' ? img : img.src || img.url) || []
            };
          })}
          totalPages={1}
          currentPage={1}
          onPageChange={() => {}}
          addToCart={(propId) => {
            const item = filteredItems.find(i => (i._original?.id || i.id) === propId);
            if (item) handleAddToBasket(item);
          }}
          setSelectedProp={(prop) => {
            const item = filteredItems.find(i => (i._original?.id || i.id) === prop.id);
            if (item) handleItemOpen(item);
          }}
          onViewDetails={(propId) => {
            if (!propId) return;
            const item = filteredItems.find(i => (i._original?.id || i.id) === propId);
            if (item) {
              const detailTarget = item._original || item;
              onPropDetailsRequest?.(detailTarget);
            } else {
              onPropDetailsRequest?.(propId);
            }
          }}
          isItemInBasket={(propId) => isItemInBasket?.(propId)}
        />
      </div>
    </div>
  );
}
